import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import ConfirmModal from "@/components/ConfirmModal";
import SkillMarkdown from "@/components/SkillMarkdown";
import { CategoryChip, threadTimeAgo } from "@/components/DiscussionBits";
import { useAuth } from "@/lib/auth-context";
import {
  deleteReply,
  deleteThread,
  getSkill,
  getThread,
  replyToThread,
  setThreadStatus,
} from "@/lib/api";
import { useSkillPath } from "@/lib/skillPath";
import { useSeoMeta } from "@/lib/useSeoMeta";
import type { Reply, SkillDetail, ThreadDetail as ThreadDetailData } from "@/lib/types";

// Full comment-tree indent, Reddit/LinkedIn-style: a reply can reply to a reply to a reply, to
// any depth. Visual indent is capped after a few levels so very deep threads don't squeeze the
// content into a sliver — depth keeps nesting logically past that point, it just stops
// indenting further.
const MAX_INDENT_DEPTH = 5;
const INDENT_PX = 26;

type ReplyNodeData = Reply & { children: ReplyNodeData[] };

function buildReplyTree(replies: Reply[]): ReplyNodeData[] {
  const byId = new Map<string, ReplyNodeData>();
  for (const r of replies) byId.set(r.id, { ...r, children: [] });
  const roots: ReplyNodeData[] = [];
  for (const r of replies) {
    const node = byId.get(r.id)!;
    const parent = r.parent_reply_id ? byId.get(r.parent_reply_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

// Replies nest to unlimited depth, and a long back-and-forth can run many levels deep. Beyond
// "a reply to a reply" (depth 2+ — i.e. a reply to a reply to a reply, and deeper), default to
// collapsed so a long sub-thread doesn't push the rest of the conversation off-screen; collapsing
// the first node past that depth is sufficient since its own children never render while it's
// collapsed, so there's no need to also list them.
//
// Crucially, a subtree is only ever auto-collapsed if EVERY reply inside it has already been
// seen by this browser on a previous visit (tracked in localStorage, see seenReplyIds below) —
// otherwise a brand-new reply nested past the collapse depth would start hidden the moment it's
// posted, which is exactly the bug this guards against (a viewer has no reason to go looking
// inside an already-collapsed subtree for a reply they don't know exists).
const DEFAULT_COLLAPSE_DEPTH = 2;

function subtreeIds(node: ReplyNodeData): string[] {
  const ids = [node.id];
  for (const c of node.children) ids.push(...subtreeIds(c));
  return ids;
}

function autoCollapseIds(nodes: ReplyNodeData[], seenIds: Set<string>, depth = 0): string[] {
  const ids: string[] = [];
  for (const n of nodes) {
    if (depth >= DEFAULT_COLLAPSE_DEPTH && n.children.length > 0) {
      if (subtreeIds(n).every((rid) => seenIds.has(rid))) ids.push(n.id);
    }
    ids.push(...autoCollapseIds(n.children, seenIds, depth + 1));
  }
  return ids;
}

function flattenIds(nodes: ReplyNodeData[]): string[] {
  const ids: string[] = [];
  for (const n of nodes) { ids.push(n.id); ids.push(...flattenIds(n.children)); }
  return ids;
}

// Per-thread "replies this browser has already displayed" — the only signal available for
// "has this user seen it" without server-side read-tracking. Read once per visit, then grown as
// new replies come in; a reply only becomes eligible to be folded into a collapsed subtree on
// some *future* visit, never the one where it first appears.
function seenStorageKey(threadId: string): string {
  return `sp-thread-seen-${threadId}`;
}
function loadSeenIds(threadId: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenStorageKey(threadId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}
function saveSeenIds(threadId: string, ids: Set<string>) {
  try {
    localStorage.setItem(seenStorageKey(threadId), JSON.stringify([...ids]));
  } catch { /* localStorage unavailable — default-collapse just won't trigger, no functional loss */ }
}

function ReplyAvatar({ name, maintainer, deleted }: { name: string | null; maintainer: boolean; deleted: boolean }) {
  const initial = deleted ? "–" : (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        deleted ? "bg-white/5 text-text/30" : maintainer ? "bg-primary/20 text-primary" : "bg-white/10 text-text/60"
      }`}
    >
      {initial}
    </span>
  );
}

function TrashIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ChevronIcon({ className = "h-3.5 w-3.5", expanded }: { className?: string; expanded: boolean }) {
  return (
    <svg
      className={`${className} transition-transform ${expanded ? "" : "-rotate-180"}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Top-level (module-scope) component — critical: if this were defined *inside* the page
// component's function body, React would treat it as a brand-new component type on every
// render (a new function reference each time), remounting the whole subtree — including the
// inline composer's <textarea> — on every keystroke. That remount is what was resetting the
// cursor to the start and reversing typed text. Keeping it at module scope gives it a stable
// identity so React just re-renders it in place instead.
function ReplyNode({
  node,
  depth,
  collapsedThreads,
  onToggleCollapse,
  replyingToId,
  inlineBody,
  inlineBusy,
  onStartReply,
  onInlineBodyChange,
  onSubmitInlineReply,
  onCancelInlineReply,
  canReply,
  canManageReply,
  onDeleteReply,
}: {
  node: ReplyNodeData;
  depth: number;
  collapsedThreads: Set<string>;
  onToggleCollapse: (id: string) => void;
  replyingToId: string | null;
  inlineBody: string;
  inlineBusy: boolean;
  onStartReply: (node: ReplyNodeData) => void;
  onInlineBodyChange: (v: string) => void;
  onSubmitInlineReply: (parentId: string) => void;
  onCancelInlineReply: () => void;
  canReply: boolean;
  canManageReply: (r: Reply) => boolean;
  onDeleteReply: (id: string) => void;
}) {
  const collapsed = collapsedThreads.has(node.id);
  const indent = Math.min(depth, MAX_INDENT_DEPTH) * INDENT_PX;

  return (
    <div style={depth > 0 ? { marginLeft: indent, borderLeft: "1px solid rgba(255,255,255,0.08)", paddingLeft: 16 } : undefined}>
      <div className="flex items-start gap-2 py-3.5">
        <ReplyAvatar name={node.author_name} maintainer={node.is_maintainer} deleted={node.deleted} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-display text-sm font-medium ${node.deleted ? "text-text/35 italic" : "text-text/85"}`}>
              {node.deleted ? "[deleted]" : node.author_name ? `@${node.author_name}` : "anonymous"}
            </span>
            {node.is_maintainer && !node.deleted && (
              <span className="rounded-[3px] border border-primary/25 bg-primary/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-primary">
                Maintainer
              </span>
            )}
            <span className="font-mono text-[11px] text-text/35">{threadTimeAgo(node.created_at)}</span>
          </div>
          <div className={`mt-1.5 text-sm ${node.deleted ? "italic text-text/35" : "text-text/75"}`}>
            {node.deleted ? node.body : <SkillMarkdown md={node.body} />}
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            {canReply && !node.deleted && (
              <button
                onClick={() => onStartReply(node)}
                className="font-display text-xs font-medium text-text/40 transition hover:text-text/70"
              >
                Reply
              </button>
            )}
            {canManageReply(node) && (
              <button
                onClick={() => onDeleteReply(node.id)}
                className="flex items-center gap-1 font-display text-xs font-medium text-text/40 transition hover:text-red-400"
              >
                <TrashIcon /> Delete
              </button>
            )}
          </div>

          {/* Inline reply composer — appears right here, at this comment's own Reply button,
              not scrolled off to a shared box elsewhere. */}
          {replyingToId === node.id && (
            <div className="mt-3">
              <textarea
                autoFocus
                value={inlineBody}
                onChange={(e) => onInlineBodyChange(e.target.value)}
                rows={3}
                placeholder={`Reply to ${node.author_name ? `@${node.author_name}` : "this comment"}…`}
                className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm outline-none focus:border-primary"
              />
              <div className="mt-2 flex items-center gap-2.5">
                <button
                  onClick={() => onSubmitInlineReply(node.id)}
                  disabled={inlineBusy || !inlineBody.trim()}
                  className="rounded-lg bg-primary px-3.5 py-1.5 font-display text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                >
                  {inlineBusy ? "Posting…" : "Reply"}
                </button>
                <button
                  onClick={onCancelInlineReply}
                  className="font-display text-xs font-medium text-text/40 transition hover:text-text/70"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {node.children.length > 0 && (
          <button
            onClick={() => onToggleCollapse(node.id)}
            title={collapsed ? `Show ${node.children.length} ${node.children.length === 1 ? "reply" : "replies"}` : "Collapse replies"}
            className="mt-3.5 flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-mono text-[11px] text-text/35 transition hover:bg-white/[0.04] hover:text-text/60"
          >
            {collapsed ? node.children.length : ""}
            <ChevronIcon expanded={!collapsed} className="h-3 w-3" />
          </button>
        )}
      </div>

      {!collapsed && node.children.map((child) => (
        <ReplyNode
          key={child.id}
          node={child}
          depth={depth + 1}
          collapsedThreads={collapsedThreads}
          onToggleCollapse={onToggleCollapse}
          replyingToId={replyingToId}
          inlineBody={inlineBody}
          inlineBusy={inlineBusy}
          onStartReply={onStartReply}
          onInlineBodyChange={onInlineBodyChange}
          onSubmitInlineReply={onSubmitInlineReply}
          onCancelInlineReply={onCancelInlineReply}
          canReply={canReply}
          canManageReply={canManageReply}
          onDeleteReply={onDeleteReply}
        />
      ))}
    </div>
  );
}

export default function ThreadDetailPage() {
  const { skillSlug, threadId } = useParams();
  const slug = String(skillSlug);
  const id = String(threadId);
  const { user, accessToken } = useAuth();
  const skillPath = useSkillPath();

  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [thread, setThread] = useState<ThreadDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDeleteThread, setConfirmDeleteThread] = useState(false);
  const [confirmDeleteReplyId, setConfirmDeleteReplyId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Which replies have their children collapsed — per-comment, not global. Auto-collapse
  // decisions (see autoCollapseIds) are made once per reply id, ever, for this page visit —
  // decidedIdsRef remembers which ids we've already decided, so a later poll/refetch (see the
  // polling effect below) never revisits — and potentially overrides — a decision already made,
  // whether that decision was "collapse" or "leave expanded because it had unseen content".
  const [collapsedThreads, setCollapsedThreads] = useState<Set<string>>(new Set());
  const decidedIdsRef = useRef<Set<string>>(new Set());
  // The one reply currently showing its inline "reply to this" composer (only one open at a time,
  // right under whichever comment you clicked "Reply" on — not a single shared box elsewhere).
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [inlineBody, setInlineBody] = useState("");
  const [inlineBusy, setInlineBusy] = useState(false);

  useSeoMeta({
    title: thread ? `${thread.title} — SkillPatch` : "Discussion — SkillPatch",
    path: `/skill/${slug}/discussions/${id}`,
    // Matches the origin's noindex for /skill/*/discussions/* — threads are still sparse, and
    // near-empty pages dilute the site's quality signals. See backend/app/services/seo.py.
    noindex: true,
  });

  const hasLoadedOnceRef = useRef(false);

  // Applies a freshly-fetched thread: updates state, then re-derives collapse decisions for any
  // reply id we haven't already decided (see autoCollapseIds' docstring for why that guard
  // matters). Shared by the initial load and every poll tick.
  const applyThread = useCallback((t: ThreadDetailData) => {
    setThread(t);
    const tree = buildReplyTree(t.replies);
    const allIds = flattenIds(tree);
    const seen = loadSeenIds(id);
    const newDecisions = autoCollapseIds(tree, seen).filter((rid) => !decidedIdsRef.current.has(rid));
    for (const rid of allIds) decidedIdsRef.current.add(rid);
    if (newDecisions.length > 0) {
      setCollapsedThreads((cur) => new Set([...cur, ...newDecisions]));
    }
    saveSeenIds(id, new Set([...seen, ...allIds]));
  }, [id]);

  // Full load — skill + thread — once on mount (and if the route params change). The skill
  // barely changes while someone's reading a thread (its name, its owner), so re-fetching the
  // whole skill document on every poll tick would be pure waste; only the thread needs to stay
  // live, via refreshThread below.
  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        getSkill(slug, accessToken),
        getThread(slug, id, accessToken),
      ]);
      setError(null);
      setSkill(s);
      hasLoadedOnceRef.current = true;
      applyThread(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load this discussion.");
    }
  }, [slug, id, accessToken, applyThread]);

  // Poll only the thread — one lean request instead of two, and the heavier of the pair (the
  // skill detail response) dropped entirely from the repeating call.
  const refreshThread = useCallback(async () => {
    try {
      applyThread(await getThread(slug, id, accessToken));
    } catch {
      // A transient poll failure shouldn't blank out an already-loaded thread — silently retry
      // on the next tick instead of surfacing an error over content that's still valid.
    }
  }, [slug, id, accessToken, applyThread]);

  useEffect(() => {
    load();
  }, [load]);

  // New replies (from anyone, in another tab) previously only showed up on a manual refresh.
  // Poll for the current state of the thread while this page stays open and visible — same
  // pattern used elsewhere in the app (e.g. the notification unread-count poll) — so a reply
  // someone else just posted appears without the viewer having to do anything. Paused while the
  // tab is backgrounded (Page Visibility API) so an idle/hidden tab doesn't keep hitting the
  // backend every 8s for nobody to see; catches up immediately on becoming visible again.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    function start() {
      if (interval) return;
      interval = setInterval(refreshThread, 8000);
    }
    function stop() {
      if (interval) { clearInterval(interval); interval = null; }
    }
    function onVisibilityChange() {
      if (document.hidden) stop();
      else { refreshThread(); start(); }
    }
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refreshThread]);

  async function submitReply() {
    if (!replyBody.trim() || !accessToken) return;
    setBusy(true);
    try {
      const updated = await replyToThread(slug, id, replyBody.trim(), accessToken, null);
      setThread(updated);
      setReplyBody("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't post your reply.");
    } finally {
      setBusy(false);
    }
  }

  async function submitInlineReply(parentId: string) {
    if (!inlineBody.trim() || !accessToken) return;
    setInlineBusy(true);
    try {
      const updated = await replyToThread(slug, id, inlineBody.trim(), accessToken, parentId);
      setThread(updated);
      setInlineBody("");
      setReplyingToId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't post your reply.");
    } finally {
      setInlineBusy(false);
    }
  }

  function startReply(node: ReplyNodeData) {
    setReplyingToId((cur) => (cur === node.id ? null : node.id));
    setInlineBody("");
  }

  async function toggleStatus() {
    if (!thread || !accessToken) return;
    setStatusBusy(true);
    try {
      const next = thread.status === "answered" ? "open" : "answered";
      const updated = await setThreadStatus(slug, id, next, accessToken);
      setThread((prev) => (prev ? { ...prev, status: updated.status } : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't update the status.");
    } finally {
      setStatusBusy(false);
    }
  }

  async function confirmDeleteThreadNow() {
    if (!accessToken) return;
    setDeleteBusy(true);
    try {
      const updated = await deleteThread(slug, id, accessToken);
      setThread((prev) => (prev ? { ...prev, ...updated } : prev));
      setConfirmDeleteThread(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete this thread.");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function confirmDeleteReplyNow() {
    if (!accessToken || !confirmDeleteReplyId) return;
    setDeleteBusy(true);
    try {
      const updated = await deleteReply(slug, id, confirmDeleteReplyId, accessToken);
      setThread(updated);
      setConfirmDeleteReplyId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete this reply.");
    } finally {
      setDeleteBusy(false);
    }
  }

  if (error) {
    return (
      <main className="px-6 py-10">
        <p className="text-sm text-red-400">{error}</p>
      </main>
    );
  }
  if (!skill || !thread) {
    return (
      <main className="px-6 py-10">
        <p className="text-sm text-text/45">Loading…</p>
      </main>
    );
  }

  const isOwner = !!user && user.id === skill.owner_id;
  // The thread's own author can also close it out / delete it — they're best placed to say
  // whether their question got answered, and it's their post either way.
  const canManageThread = !thread.deleted && (isOwner || (!!user && user.id === thread.author_id));
  const canReply = !!user;
  const tree = buildReplyTree(thread.replies);

  function canManageReply(r: Reply) {
    return !r.deleted && !!user && (user.id === r.author_id || isOwner);
  }

  function toggleCollapse(replyId: string) {
    setCollapsedThreads((cur) => {
      const next = new Set(cur);
      if (next.has(replyId)) next.delete(replyId);
      else next.add(replyId);
      return next;
    });
  }

  return (
    <main className="px-6 py-10">
      <p className="font-mono text-xs text-text/40">
        <Link to={skillPath(slug)} className="hover:text-text/70">{skill.name}</Link>
        {" / "}
        <Link to={skillPath(slug, "?tab=discussions")} className="hover:text-text/70">Discussions</Link>
        {" / Thread"}
      </p>

      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2.5">
          <CategoryChip category={thread.category} />
          <span className="font-mono text-[11.5px] text-text/40">
            {thread.deleted ? "[deleted]" : thread.author_name ? `@${thread.author_name}` : "anonymous"} · {threadTimeAgo(thread.created_at)}
          </span>
        </div>
        <h1 className="mt-3 font-display text-lg font-medium text-text">{thread.title}</h1>
        <div className="mt-3">
          {thread.deleted ? (
            <p className="text-sm italic text-text/35">{thread.body}</p>
          ) : thread.body ? (
            <SkillMarkdown md={thread.body} />
          ) : null}
        </div>
        {canManageThread && (
          <div className="mt-4 flex items-center gap-2.5">
            <button
              onClick={toggleStatus}
              disabled={statusBusy}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-display text-xs font-medium transition disabled:opacity-50 ${
                thread.status === "answered"
                  ? "border-white/15 text-text/60 hover:border-white/30"
                  : "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20"
              }`}
            >
              {thread.status === "answered" ? "Reopen" : "Mark as answered"}
            </button>
            <button
              onClick={() => setConfirmDeleteThread(true)}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 font-display text-xs font-medium text-text/50 transition hover:border-red-400/40 hover:text-red-400"
            >
              <TrashIcon /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Top-level composer — for replying to the post itself, not to a specific comment
          (those get their own inline box right at that comment's Reply button, below). */}
      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5">
        {user ? (
          <div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/40">Write a reply</p>
            <textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={4}
              placeholder="Share what you know…"
              className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm outline-none focus:border-primary"
            />
            <button
              onClick={submitReply}
              disabled={busy || !replyBody.trim()}
              className="mt-3 rounded-lg bg-primary px-4 py-2 font-display text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Posting…" : "Reply"}
            </button>
          </div>
        ) : (
          <p className="text-sm text-text/45">
            <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="text-primary hover:underline">
              Log in
            </Link>{" "}
            to reply.
          </p>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
        <span className="h-px w-5 bg-primary" />
        {thread.replies.length} {thread.replies.length === 1 ? "reply" : "replies"}
      </div>

      <div className="mt-1">
        {tree.map((node, i) => (
          <div key={node.id} className={i > 0 ? "border-t border-white/[0.06]" : ""}>
            <ReplyNode
              node={node}
              depth={0}
              collapsedThreads={collapsedThreads}
              onToggleCollapse={toggleCollapse}
              replyingToId={replyingToId}
              inlineBody={inlineBody}
              inlineBusy={inlineBusy}
              onStartReply={startReply}
              onInlineBodyChange={setInlineBody}
              onSubmitInlineReply={submitInlineReply}
              onCancelInlineReply={() => setReplyingToId(null)}
              canReply={canReply}
              canManageReply={canManageReply}
              onDeleteReply={setConfirmDeleteReplyId}
            />
          </div>
        ))}
      </div>

      {confirmDeleteThread && (
        <ConfirmModal
          title="Delete this discussion?"
          message="The title stays visible so the thread doesn't just disappear mid-conversation, but your post's body and name will show as “[deleted]”. Replies are unaffected."
          confirmLabel="Delete"
          destructive
          busy={deleteBusy}
          onConfirm={confirmDeleteThreadNow}
          onCancel={() => setConfirmDeleteThread(false)}
        />
      )}
      {confirmDeleteReplyId && (
        <ConfirmModal
          title="Delete this reply?"
          message="It'll show as “[deleted]” so any replies underneath it keep their place. This can't be undone."
          confirmLabel="Delete"
          destructive
          busy={deleteBusy}
          onConfirm={confirmDeleteReplyNow}
          onCancel={() => setConfirmDeleteReplyId(null)}
        />
      )}
    </main>
  );
}
